import type { AccessLevelProp, BadgeProps, IconName } from '@meterup/atto';
import type { ExtractRouteParams } from '@meterup/react-router-extensions';
import type {
  HTMLAttributeAnchorTarget,
  HTMLAttributes,
  MouseEventHandler,
  ReactNode,
} from 'react';
import { NavigationMenu } from '@base-ui/react/navigation-menu';
import {
  colors,
  darkThemeSelector,
  styled,
  useAccessLevelContext,
  UserAccessLevelContextProvider,
} from '@meterup/atto';
import { memo, useCallback, useMemo } from 'react';
import { type To } from 'react-router';

import { dockValues } from '../shared';
import { SidebarDivider } from './SidebarAtoms';
import { SidebarTarget } from './SidebarTarget';
import {
  sidebarTreeFlyoutListStyle,
  SidebarTreeFlyoutPopupFrame,
  SidebarTreeFlyoutViewportFrame,
  useSidebarTreeFlyoutShared,
} from './SidebarTreeFlyout';
import { useSidebarContext, useSidebarTreesContext } from './utils';
import { sidebarVars } from './vars';

export const SidebarTreeBranches = styled('div', {
  position: 'relative',
  flexDirection: 'column',
  padding: `0 0 $4 $${sidebarVars.padding.branches}`,

  '&::after, &::before': {
    content: '',
    position: 'absolute',
    display: 'block',
    background: colors.strokeNeutralLight,
    borderRadius: '99em',

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  '&::before': {
    top: '$4',
    left: `$${sidebarVars.padding.branches - 9}`,
    bottom: '$8',
    widthAll: '$2',
  },

  '&::after': {
    left: `$${sidebarVars.padding.branches - 9 - 2}`,
    bottom: '$8',
    widthAll: '$6',
    heightAll: '$2',
  },

  variants: {
    isOpen: {
      true: {
        display: 'flex',
      },
      false: {
        display: 'none',
      },
    },
  },
});
SidebarTreeBranches.displayName = 'SidebarTreeBranches';

export const SidebarTreeContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  padding: `0 $${sidebarVars.padding.sides}`,

  variants: {
    isOpen: {
      true: {},
      false: {},
    },
    minimized: {
      true: {},
      false: {},
    },
  },

  defaultVariants: {
    isOpen: false,
  },
});
SidebarTreeContainer.displayName = 'SidebarTreeContainer';

const SidebarFlyoutBranchPanelHeader = styled('div', {
  padding: '$4 $4 0',
});
SidebarFlyoutBranchPanelHeader.displayName = 'SidebarFlyoutBranchPanelHeader';

const SidebarFlyoutBranchPanelList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  padding: '$4',
});
SidebarFlyoutBranchPanelList.displayName = 'SidebarFlyoutBranchPanelList';

const SidebarFlyoutBranchPanel = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  '@notDesktop': {
    widthAll: `$${dockValues.width.maximized['@tablet']}`,
  },

  '@desktop': {
    widthAll: `$${dockValues.width.maximized['@desktop']}`,
  },
});
SidebarFlyoutBranchPanel.displayName = 'SidebarFlyoutBranchPanel';

const SidebarNavTreeItem = styled(NavigationMenu.Item, {
  width: '100%',
  listStyle: 'none',
});
SidebarNavTreeItem.displayName = 'SidebarNavTreeItem';

const SidebarNavFlyoutContentMotion = styled(NavigationMenu.Content, {
  outline: 'none',
  transitionProperty: 'opacity, transform',
  transitionDuration: '280ms',
  transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
  '&[data-starting-style]': {
    opacity: 0,
  },
  '&[data-ending-style]': {
    opacity: 0,
  },
  '&[data-starting-style][data-activation-direction="right"]': {
    transform: 'translateX(14px)',
  },
  '&[data-starting-style][data-activation-direction="left"]': {
    transform: 'translateX(-14px)',
  },
  '&[data-starting-style][data-activation-direction="down"]': {
    transform: 'translateY(14px)',
  },
  '&[data-starting-style][data-activation-direction="up"]': {
    transform: 'translateY(-14px)',
  },
  '&[data-ending-style][data-activation-direction="right"]': {
    transform: 'translateX(-14px)',
  },
  '&[data-ending-style][data-activation-direction="left"]': {
    transform: 'translateX(14px)',
  },
  '&[data-ending-style][data-activation-direction="down"]': {
    transform: 'translateY(-14px)',
  },
  '&[data-ending-style][data-activation-direction="up"]': {
    transform: 'translateY(14px)',
  },
});
SidebarNavFlyoutContentMotion.displayName = 'SidebarNavFlyoutContentMotion';

export type SidebarTreeIDProp = string;

export interface StaticSidebarTreeBranchProps<Path extends string = string> {
  accessLevel?: AccessLevelProp | null;
  badge?: Omit<BadgeProps, 'size'>;
  icon?: IconName;
  label: ReactNode;
  subgroup?: string;

  path: Path;
  end?: boolean;
  linkArgs?: Omit<ExtractRouteParams<Path, string>, 'companyName' | 'networkSlug'>;
  selected?: boolean;
}

export interface StaticSidebarTreeBranchGroupProps {
  group: string;
  accessLevel?: AccessLevelProp | null;
  branches: StaticSidebarTreeBranchProps[];
}

export interface StaticSidebarTreeRootProps<Path extends string = string>
  extends StaticSidebarTreeBranchProps<Path> {
  onClick?: () => void;
}

export interface StaticSidebarTreeProps {
  linkArgs?: {
    companyName: string;
    networkSlug: string;
  };

  /**
   * The ID of the group.
   */
  id: SidebarTreeIDProp;
  /**
   * The root of the group.
   */
  root: StaticSidebarTreeRootProps;
  /**
   * The branches of the group. Can be individual branches or grouped branches.
   */
  branches?: (StaticSidebarTreeBranchProps | StaticSidebarTreeBranchGroupProps)[];
}

export interface SidebarTreeBranchProps {
  accessLevel?: AccessLevelProp | null;
  badge?: Omit<BadgeProps, 'size'>;
  icon?: IconName;
  internal?: boolean;
  label: ReactNode;
  subgroup?: string;

  /** If omitted and `path` provided, will use route matching. */
  selected?: boolean;

  // Routing
  as?: React.ElementType;
  to?: To;
  href?: string;
  target?: HTMLAttributeAnchorTarget;
  rel?: string;
}

export interface SidebarTreeBranchGroupProps {
  group: string;
  accessLevel?: AccessLevelProp | null;
  branches: SidebarTreeBranchProps[];
}

export function isStaticSidebarTreeBranchGroup(
  branch: StaticSidebarTreeBranchProps | StaticSidebarTreeBranchGroupProps,
): branch is StaticSidebarTreeBranchGroupProps {
  return 'branches' in branch && Array.isArray(branch.branches);
}

export function isSidebarTreeBranchGroup(
  branch: SidebarTreeBranchProps | SidebarTreeBranchGroupProps,
): branch is SidebarTreeBranchGroupProps {
  return 'branches' in branch && Array.isArray(branch.branches);
}

function sidebarTreeBranchRowKey(
  treeId: SidebarTreeIDProp,
  groupName: string,
  branch: SidebarTreeBranchProps,
): string {
  const label = branch.label?.toString() ?? '';
  let toPart = '';
  if (typeof branch.to === 'string') {
    toPart = branch.to;
  } else if (branch.to != null) {
    toPart = JSON.stringify(branch.to);
  }
  return [treeId, groupName, branch.subgroup ?? '', toPart, label].join('|');
}

export interface SidebarTreeRootProps extends SidebarTreeBranchProps {
  onClick?: MouseEventHandler<HTMLElement>;
}

export type SidebarTreeSize = 'medium' | 'large' | 'x-large';

export interface SidebarTreeProps {
  /**
   * The ID of the group.
   */
  id: SidebarTreeIDProp;
  /**
   * The root of the group.
   */
  root: SidebarTreeRootProps;
  /**
   * The branches of the group. Can be individual branches or grouped branches.
   */
  branches?: (SidebarTreeBranchProps | SidebarTreeBranchGroupProps)[];
  /**
   * Whether the dock is minimized.
   */
  minimized?: boolean;
}

function SidebarTreeComponent({ id, root, branches, minimized = false }: SidebarTreeProps) {
  const accessLevel = useAccessLevelContext(root.accessLevel);

  const { collapsed: contextCollapsed, collapsible, setCollapsed } = useSidebarTreesContext();
  const { setAllTreesCollapsed } = useSidebarContext();

  const hasBranches = Boolean(branches?.length);

  const hasSelectedBranch = useMemo(() => {
    if (!hasBranches) return false;
    return (
      branches?.some((branch) => {
        if (isSidebarTreeBranchGroup(branch)) {
          return branch.branches.some((l) => l.selected);
        }
        return Boolean(branch.selected);
      }) ?? false
    );
  }, [hasBranches, branches]);

  const sectionActive = hasSelectedBranch;

  const collapsed = collapsible ? contextCollapsed : !sectionActive;

  const canToggleTree = Boolean(collapsible && hasBranches);

  const { onClick: rootOnClick, as: rootAs, to: rootTo } = root;

  const handleRootClick = useCallback<MouseEventHandler<HTMLElement>>(
    (event) => {
      if (!canToggleTree) {
        rootOnClick?.(event);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        rootOnClick?.(event);
        return;
      }
      if (rootAs && rootTo) {
        event.preventDefault();
      }
      // Shift+click broadcasts this tree's next state to every tree, so it acts
      // as a shortcut to collapse or expand all trees at once.
      if (event.shiftKey) {
        setAllTreesCollapsed(!contextCollapsed);
        return;
      }
      setCollapsed(!contextCollapsed);
    },
    [
      canToggleTree,
      contextCollapsed,
      rootAs,
      rootOnClick,
      rootTo,
      setCollapsed,
      setAllTreesCollapsed,
    ],
  );

  const useMinimizedBranchFlyout = minimized && hasBranches;
  const flyoutShared = useSidebarTreeFlyoutShared();
  const useMinimizedSharedFlyout = useMinimizedBranchFlyout && flyoutShared;
  const useMinimizedIsolatedFlyout = useMinimizedBranchFlyout && !flyoutShared;

  const branchNodes = useMemo(() => {
    if (!branches?.length) {
      return { column: null as ReactNode, flyout: null as ReactNode };
    }
    const build = (branchMinimized: boolean) => {
      let sidebarGroupOrdinal = 0;

      const leafTarget = (groupName: string, b: SidebarTreeBranchProps) => (
        <SidebarTarget
          key={sidebarTreeBranchRowKey(id, groupName, b)}
          as={b.as}
          to={b.to}
          accessLevel={b.accessLevel}
          annotations={b.badge ? { badges: [b.badge] } : undefined}
          media={
            b.icon ? { icon: { icon: b.icon } } : { icon: { icon: 'placeholder' as IconName } }
          }
          label={b.label}
          minimized={branchMinimized}
          selected={b.selected}
          internal={b.internal ?? false}
        />
      );

      return branches.flatMap((branch) => {
        if (isSidebarTreeBranchGroup(branch)) {
          const showDivider = sidebarGroupOrdinal > 0;
          const dividerKey = `${id}-sidebar-group-divider-${sidebarGroupOrdinal}`;
          sidebarGroupOrdinal += 1;
          return [
            ...(showDivider ? [<SidebarDivider key={dividerKey} nested />] : []),
            ...branch.branches.map((b) => leafTarget(branch.group, b)),
          ];
        }

        return leafTarget('_', branch);
      });
    };

    return { column: build(minimized), flyout: build(false) };
  }, [branches, id, minimized]);

  const minimizedBranchFlyoutNavItem = useMemo(() => {
    if (!hasBranches) {
      return null;
    }

    return (
      <SidebarNavTreeItem value={id}>
        <NavigationMenu.Trigger
          nativeButton={false}
          render={(props: HTMLAttributes<HTMLElement>) => (
            <SidebarTarget
              id={`${id}-root`}
              as={root.as}
              to={root.to}
              accessLevel={root.accessLevel}
              annotations={root.badge ? { badges: [root.badge] } : undefined}
              isOpen={!collapsed}
              label={root.label}
              media={{ icon: { icon: root.icon as IconName } }}
              arrows={canToggleTree ? 'toggle' : undefined}
              minimized={minimized}
              selected={(minimized || collapsed) && root.selected}
              internal={root.internal ?? false}
              onClick={canToggleTree || root.onClick ? handleRootClick : undefined}
              composeProps={props as HTMLAttributes<HTMLElement>}
              suppressMinimizedTooltip
            />
          )}
        />
        <SidebarNavFlyoutContentMotion>
          <SidebarFlyoutBranchPanel
            role="list"
            aria-labelledby={`${id}-root`}
            id={`${id}-branches-flyout`}
          >
            <SidebarFlyoutBranchPanelHeader>
              <SidebarTarget
                minimized={false}
                media={{ icon: { icon: root.icon as IconName } }}
                label={root.label}
              />
            </SidebarFlyoutBranchPanelHeader>
            <SidebarDivider nested />
            <SidebarFlyoutBranchPanelList>{branchNodes.flyout}</SidebarFlyoutBranchPanelList>
          </SidebarFlyoutBranchPanel>
        </SidebarNavFlyoutContentMotion>
      </SidebarNavTreeItem>
    );
  }, [
    branchNodes.flyout,
    canToggleTree,
    collapsed,
    handleRootClick,
    hasBranches,
    id,
    minimized,
    root,
  ]);

  if (root.accessLevel === null) {
    return null;
  }

  const renderTreeBody = () => {
    if (useMinimizedSharedFlyout) {
      return minimizedBranchFlyoutNavItem;
    }

    if (useMinimizedIsolatedFlyout) {
      return (
        <NavigationMenu.Root orientation="vertical" delay={80} closeDelay={150}>
          <SidebarTreeContainer
            id={id}
            minimized={minimized}
            role="group"
            data-navigation-group
            aria-label={`${root?.label} group`}
            isOpen={!collapsed}
          >
            <NavigationMenu.List style={sidebarTreeFlyoutListStyle}>
              {minimizedBranchFlyoutNavItem}
            </NavigationMenu.List>
          </SidebarTreeContainer>
          <NavigationMenu.Portal>
            <NavigationMenu.Positioner
              side="right"
              sideOffset={12}
              align="start"
              collisionPadding={8}
            >
              <SidebarTreeFlyoutPopupFrame>
                <SidebarTreeFlyoutViewportFrame />
              </SidebarTreeFlyoutPopupFrame>
            </NavigationMenu.Positioner>
          </NavigationMenu.Portal>
        </NavigationMenu.Root>
      );
    }

    return (
      <SidebarTreeContainer
        id={id}
        minimized={minimized}
        role="group"
        data-navigation-group
        aria-label={`${root?.label} group`}
        isOpen={!collapsed}
      >
        <SidebarTarget
          id={`${id}-root`}
          as={root.as}
          to={root.to}
          accessLevel={root.accessLevel}
          annotations={root.badge ? { badges: [root.badge] } : undefined}
          isOpen={!collapsed}
          label={root.label}
          media={{ icon: { icon: root.icon as IconName } }}
          arrows={canToggleTree ? 'toggle' : undefined}
          minimized={minimized}
          selected={(minimized || collapsed) && root.selected}
          internal={root.internal ?? false}
          onClick={canToggleTree || root.onClick ? handleRootClick : undefined}
        />
        {hasBranches && (
          <SidebarTreeBranches
            isOpen={!collapsed}
            role="list"
            aria-labelledby={`${id}-root`}
            id={`${id}-branches`}
            aria-hidden={collapsed}
          >
            {branchNodes.column}
          </SidebarTreeBranches>
        )}
      </SidebarTreeContainer>
    );
  };

  return (
    <UserAccessLevelContextProvider value={accessLevel}>
      {renderTreeBody()}
    </UserAccessLevelContextProvider>
  );
}

export const SidebarTree = memo(SidebarTreeComponent);
